
import { TCubeDimension, TCubeMeasure } from '@cubejs-client/core';
import { ApexOptions } from 'apexcharts';
import { formatMessage } from 'devextreme/localization';

export type ApexChartType = ApexChart['type'];
// export type AvailableChartTypes = Extract<ApexChartType, 'pie' | 'bar' | 'line' | 'area' | 'donut'> | 'table';

export type AvailableChartTypes =
	| 'area'
	| 'bar'
	| 'donut'
	| 'line'
	| 'pie'
	| 'table'
	| 'radar'
	;


export interface IChartOptions {
	apexOptions: ApexOptions
	chartType: AvailableChartTypes
	config: IChartConfig | IChartConfig[]

	languageCode: string // do not save on db
	translate: typeof formatMessage // do not save on db
}

export enum ChartConfigVersion {
	/**
	 * example object
	 *
	 * ```ts
	 * {
	 * 	colors: [ '#6AF' ]
	 * }
	 * ```
	 *
	 * Added props:
	 *
	 *  - colors
	 *
	 */
	Beta = 0,
	/**
	 * example objet
	 * ```ts
	 * {
	 * 	axisDimensions: [], // Array of TCubeDimension
	 * 	colors: [ '#6AF' ],
	 * 	isDonut: false,
	 * 	isGroupedByWeek: false,
	 * 	isPercentage: false,
	 * 	isStacked: false,
	 * 	legendDimensions: [],
	 * 	measures: [],
	 * 	selectedDimensions: [],
	 * 	version: 1
	 * }
	 * ```
	 *
	 * Added props:
	 *
	 * - axisDimensions
	 * - isDonut
	 * - isGroupedByWeek
	 * - isPercentage
	 * - isStacked
	 * - legendDimensions
	 * - measures
	 * - selectedDimensions
	 * - version
	 */
	AxisAndLegends = 1
}
export type LatestChartConfigVersion = ChartConfigVersion.AxisAndLegends;

export interface IChartConfig {

	/**
	 * Dimensions selected for the axis
	 * @since 1 - AxisAndLegends
	 *
	 * @type {TCubeDimension[]}
	 * @memberof IChartConfig
	 */
	axisDimensions: TCubeDimension[]

	/**
	 * Colors of the legends
	 * @since 0 - Beta
	 *
	 * @type {any[]}
	 * @memberof IChartConfig
	 */
	colors: any[]

	isDonut: boolean,
	isPercentage: boolean,
	isStacked: boolean,
	isHorizontal: boolean,
	isMultiple: boolean

	/**
	 * Dimensions selected for the legend
	 * @since 1 - AxisAndLegends
	 *
	 * @type {TCubeDimension[]}
	 * @memberof IChartConfig
	 */
	legendDimensions: TCubeDimension[]
	/**
	 * Selected measures
	 * @since 1 - AxisAndLegends
	 *
	 * @type {TCubeMeasure[]}
	 * @memberof IChartConfig
	 */
	measures: TCubeMeasure[]
	/**
	 * Selected dimensions
	 * @since 1 - AxisAndLegends
	 *
	 * @type {TCubeDimension[]}
	 * @memberof IChartConfig
	 */
	selectedDimensions: TCubeDimension[]

	/**
	 * Version of the IChartConfig, usefull to manage edge cases when new options are added.
	 * For IChartConfig versions older than 1.0.0 is undefined
	 * @since 1 - AxisAndLegends
	 *
	 * @type {ChartConfigVersion}
	 * @memberof IChartConfig
	 */
	version?: ChartConfigVersion

	idQuery?: number

	title?: string
}

export interface IChartResult {
	series: IChartSerie[] | number[]
	options: ApexOptions
	queryIndexes?: number[]
}

export interface IChartSerie {
	data: number[]
	measure: string
	name: string
	nameCode: string
}

// export interface IChartSeriePoint {
// 	x: string
// 	y: number
// }

export function getLatestChartConfigVersion(): LatestChartConfigVersion {
	return ChartConfigVersion.AxisAndLegends;
}
export function getDefaultChartConfig(): IChartConfig {
	return {
		axisDimensions: [],
		colors: [],
		isDonut: false,
		isPercentage: false,
		isStacked: false,
		isHorizontal: false,
		isMultiple: false,
		legendDimensions: [],
		measures: [],
		selectedDimensions: [],
		title: ''
	};
}

export function getDefaultLatestVersionChartConfig(): IChartConfig {
	const config = getDefaultChartConfig();
	config.version = getLatestChartConfigVersion();
	return config;
}

export type SimilarToArray<T> = {
	readonly [n: number]: T;
}

